import { Component } from '@theme/component';
import { CartAddEvent, SlideshowSelectEvent } from '@theme/events';
import { CartController } from './cart.js';

const RESET_DELAY = 2500;

/**
 * @typedef {object} ShopRitualAddAllRefs
 * @property {HTMLButtonElement} button - The "add all to bag" button.
 * @property {HTMLElement} label - The button label.
 * @property {HTMLElement} [liveRegion] - The live region announcing the result.
 */

/**
 * Adds every product of the ritual to the cart in a single cart/add request.
 *
 * @extends {Component<ShopRitualAddAllRefs>}
 */
class ShopRitualAddAll extends Component {
  requiredRefs = ['button', 'label'];

  /** @type {boolean} */
  #pending = false;

  /** @type {string | undefined} */
  #initialLabel;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timeout;

  /** @returns {{ id: number, quantity: number }[]} */
  get items() {
    return (this.dataset.variantIds ?? '')
      .split(',')
      .map((id) => Number(id.trim()))
      .filter((id) => Number.isFinite(id) && id > 0)
      .map((id) => ({ id, quantity: 1 }));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#timeout) clearTimeout(this.#timeout);
  }

  /**
   * Returns the button to its idle state
   * so an "Added to bag" label never carries over to the next slide's product list
   */
  reset() {
    const { label, liveRegion } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);
    this.dataset.state = 'idle';

    if (label && this.#initialLabel != null) label.textContent = this.#initialLabel;
    if (liveRegion) liveRegion.textContent = '';
  }

  /**
   * Adds all of the section's products to the cart.
   * @param {Event} [event] - The click event.
   */
  async addAll(event) {
    event?.preventDefault();

    const items = this.items;
    if (this.#pending || items.length === 0) return;

    const { button, label, liveRegion } = this.refs;
    const initialLabel = this.#initialLabel ?? label.textContent ?? '';
    this.#initialLabel = initialLabel;

    this.#pending = true;
    if (this.#timeout) clearTimeout(this.#timeout);
    button.disabled = true;
    this.dataset.state = 'loading';

    try {
      const cart = await CartController.add(items);

      // A cart error response carries a `status` — the theme's cart controller resolves it as data.
      if (cart?.status) throw new Error(cart.description || cart.message || 'Cart error');

      this.dataset.state = 'added';
      label.textContent = this.dataset.addedText || initialLabel;
      if (liveRegion) liveRegion.textContent = label.textContent;

      document.dispatchEvent(
        new CartAddEvent(cart, this.id || 'shop-ritual-add-all', {
          source: 'shop-ritual-add-all',
          itemCount: items.length,
        })
      );
    } catch (error) {
      console.error('Shop ritual: unable to add all items to the cart', error);
      this.dataset.state = 'error';
    } finally {
      this.#pending = false;
      button.disabled = false;

      this.#timeout = setTimeout(() => {
        this.dataset.state = 'idle';
        label.textContent = initialLabel;
        if (liveRegion) liveRegion.textContent = '';
      }, RESET_DELAY);
    }
  }
}

if (!customElements.get('shop-ritual-add-all')) {
  customElements.define('shop-ritual-add-all', ShopRitualAddAll);
}

/**
 * @typedef {object} ShopRitualSyncRefs
 * @property {HTMLElement[]} [eyebrows] - One eyebrow per ritual block.
 * @property {HTMLElement[]} [groups] - One product list per ritual block.
 * @property {HTMLElement} [addAll] - The "add all to bag" button.
 */

/**
 * Keeps the ritual's eyebrow and product list in sync with the media slider.
 *
 * Every block's eyebrow and list is already in the DOM, so this only swaps which one is visible —
 * the product forms are never re-created and quick add keeps working.
 *
 * @extends {Component<ShopRitualSyncRefs>}
 */
class ShopRitualSync extends Component {
  /** @type {number} */
  #index = 0;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideSelect);

    // Selecting a block in the theme editor should bring up its slide.
    if (Shopify.designMode) this.addEventListener('shopify:block:select', this.#handleBlockSelect);

    this.#activate(this.#slideshow?.current ?? 0);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#activate(this.#slideshow?.current ?? this.#index);
  }

  /** @returns {(HTMLElement & { current?: number, slides?: Element[], select?: (index: number) => void }) | null} */
  get #slideshow() {
    return this.querySelector('slideshow-component');
  }

  /** @param {Event} event */
  #handleSlideSelect = (event) => {
    const index = /** @type {{ detail?: { index?: unknown } }} */ (event).detail?.index;
    if (typeof index !== 'number') return;

    // Ignore slideshows belonging to a nested ritual section.
    if (event.target instanceof Element && event.target.closest('shop-ritual-sync') !== this) return;

    this.#activate(index);
  };

  /** @param {Event} event */
  #handleBlockSelect = (event) => {
    if (!(event.target instanceof Element)) return;

    const slide = event.target.closest('slideshow-slide');
    const slideshow = this.#slideshow;
    if (!slide || !slideshow?.slides || typeof slideshow.select !== 'function') return;

    const index = slideshow.slides.indexOf(slide);
    if (index >= 0 && index !== slideshow.current) slideshow.select(index);
  };

  /**
   * Shows the eyebrow and the product list belonging to a slide.
   * @param {number} index - The 0-based slide index.
   */
  #activate(index) {
    this.#index = Number.isFinite(index) && index > 0 ? Math.floor(index) : 0;

    const eyebrows = this.refs.eyebrows ?? [];
    const activeEyebrow = clampIndex(this.#index, eyebrows.length);

    eyebrows.forEach((eyebrow, position) => {
      eyebrow.toggleAttribute('data-active', position === activeEyebrow);
    });

    const groups = this.refs.groups ?? [];
    const activeGroup = clampIndex(this.#index, groups.length);

    groups.forEach((group, position) => {
      group.hidden = position !== activeGroup;
    });

    this.#syncAddAll(groups[activeGroup]);
  }

  /**
   * Points the "add all to bag" button at the variants of the visible product list.
   * @param {HTMLElement} [group] - The visible product list.
   */
  #syncAddAll(group) {
    const addAll = this.refs.addAll;
    if (!addAll) return;

    const ids = group
      ? Array.from(group.querySelectorAll('[data-variant-id]'), (card) => card.getAttribute('data-variant-id') ?? '')
          .filter(Boolean)
          .join(',')
      : '';

    addAll.dataset.variantIds = ids;
    addAll.hidden = ids === '';

    if (addAll instanceof ShopRitualAddAll) addAll.reset();
  }
}

/**
 * Clamps a slide index to a collection's bounds, so a stray index can never hide everything.
 *
 * @param {number} index - The 0-based slide index.
 * @param {number} length - The number of items in the collection.
 * @returns {number} The clamped index.
 */
function clampIndex(index, length) {
  if (length < 1) return 0;

  return Math.min(index, length - 1);
}

if (!customElements.get('shop-ritual-sync')) {
  customElements.define('shop-ritual-sync', ShopRitualSync);
}
